import axios from 'axios';
import ThemeButton from 'components/AntdFormInputs/theme-button';
import { parseHtml } from 'helpers';
import React, { useEffect, useState } from 'react';
import { Check, Copy, X } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAiContext,
  getContent,
  getOpenModal,
  setContent,
  setEditorValue,
  setOpenModal,
} from 'store/ai-modal';
import TypingEffect from './typing-effect';
import { TbPlaylistAdd } from 'react-icons/tb';
import { useClipboard } from 'hooks/use-clipboard';

const AiModalPopup = () => {
  const isOpenModal = useSelector(getOpenModal);
  const { clipboardCopy, copied } = useClipboard();
  const aiContext = useSelector(getAiContext);
  const isContent = useSelector(getContent);
  const dispatch = useDispatch();

  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(false);

  const getPlainTextFromHTML = (html) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };
  const checkingContent = getPlainTextFromHTML(isContent)?.length;

  const toneOptions = {
    general: 'General',
    formal: 'Formal',
    funny: 'Funny',
    conversational: 'Conversational',
    professional: 'Professional',
    persuasive: 'Persuasive',
    motivational: 'Motivational',
    sarcastic: 'Sarcastic',
    poetic: 'Poetic',
    simple: 'Simple',
    technical: 'Technical',
    storytelling: 'Storytelling',
    excited: 'Excited',
  };
  const handleClearTexts = () => {
    setSuggestion(null);
    dispatch(setContent(null));
    dispatch(setOpenModal(false));
  };

  useEffect(() => {
    if (isOpenModal && checkingContent) {
      handlePromptSubmit('general');
    }
  }, [isOpenModal]);

  const handlePromptSubmit = async (tone) => {
    const text = getPlainTextFromHTML(isContent);
    if (text.length > 10) {
      setLoading(true);
      try {
        const promptContent = `You are an internationally acclaimed English teacher. Your grammar, punctuation, spelling, and sentence structure corrections are always accurate and professional. IMPORTANT: Only return the corrected or rewritten text without any explanation, comments, or formatting.`;

        const promptMap = {
          general: `Correct the grammar and punctuation of the following sentence without changing its meaning. Only return the corrected text:\n\n"${text}"`,
          formal: `Rewrite the following text in a formal tone. Only return the rewritten text:\n\n"${text}"`,
          funny: `Rewrite the following text in a humorous tone. Only return the rewritten text:\n\n"${text}"`,
          conversational: `Rewrite the following text in a casual tone. Only return the rewritten text:\n\n"${text}"`,
          professional: `Rewrite the following text to sound professional. Only return the rewritten text:\n\n"${text}"`,
          persuasive: `Rewrite the following text to make it more persuasive. Only return the rewritten text:\n\n"${text}"`,
          empathetic: `Rewrite the following text with a warm and understanding tone. Only return the rewritten text:\n\n"${text}"`,
          motivational: `Rewrite the following text in an inspiring tone. Only return the rewritten text:\n\n"${text}"`,
          sarcastic: `Rewrite the following text in a sarcastic tone. Only return the rewritten text:\n\n"${text}"`,
          poetic: `Rewrite the following text with poetic expression. Only return the rewritten text:\n\n"${text}"`,
          simple: `Simplify the following text. Only return the simplified text:\n\n"${text}"`,
          technical: `Rewrite the following in a technical tone. Only return the rewritten text:\n\n"${text}"`,
          storytelling: `Turn the following into a storytelling format. Only return the rewritten story:\n\n"${text}"`,
          excited: `Rewrite the following with excitement. Only return the rewritten text:\n\n"${text}"`,
        };
        const response = await axios.post(
          'https://openrouter.ai/api/v1/chat/completions',
          {
            model: 'mistralai/mistral-7b-instruct:free',
            messages: [
              {
                role: 'system',
                content: promptContent,
              },
              {
                role: 'user',
                content: promptMap[tone],
              },
            ],
            max_tokens: 300,
          },
          {
            headers: {
              Authorization: `Bearer ${process.env.NEXT_PUBLIC_OPENROUTER_API_KEY}`,
              'Content-Type': 'application/json',
            },
          },
        );

        const result =
          response.data.choices?.[0]?.message?.content?.trim() ||
          'No suggestion received.';
        setSuggestion(result);
      } catch (error) {
        console.error(
          'Error fetching suggestion:',
          error.response?.data || error.message,
        );
        setSuggestion('❌ Error fetching suggestion');
      } finally {
        setLoading(false);
      }
    } else {
      setSuggestion('');
    }
  };

  const handleCopy = () => {
    if (suggestion) {
      clipboardCopy(suggestion);
    }
  };

  const handleInsert = () => {
    if (suggestion && aiContext?.full && aiContext?.selectedHtml) {
      const replacedHtml = aiContext.full.replace(
        aiContext.selectedHtml,
        suggestion,
      );
      dispatch(setEditorValue(replacedHtml));
      dispatch(setOpenModal(false));
    } else if (suggestion) {
      dispatch(setEditorValue(suggestion));
      dispatch(setOpenModal(false));
    }
  };
  return (
    <>
      {isOpenModal ? (
        <div className="ai-popup-wrapper">
          <div className="ai-gradient-bg" />
          <div className="ai-modal">
            <div className="ai-header-modal">
              <h2>ChampsLms AI</h2>
              <ThemeButton
                padding="4px 4px"
                onClick={handleClearTexts}
                variant="cancel"
              >
                <X />
              </ThemeButton>
            </div>
            <hr />
            <section className="ai-text-editor-placeholder">
              {checkingContent ? (
                <div className="texteditor-replytext">
                  {parseHtml(isContent)}
                </div>
              ) : (
                'No content found. Please enter some text...'
              )}
              {suggestion ? <hr /> : null}
              {loading ? (
                ''
              ) : suggestion ? (
                <>
                  <div className="custom-bot-reply">
                    <svg
                      class="custom-ai-assistan-icon"
                      fill="#FA5252"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="24px"
                      height="24px"
                    >
                      <path d="M12,0C11.743,6.518,6.518,11.743,0,12c6.518,0.257,11.743,5.482,12,12c0.257-6.518,5.482-11.743,12-12	C17.482,11.743,12.257,6.518,12,0z M12,17.163C10.743,15.039,8.961,13.257,6.837,12C8.961,10.743,10.743,8.961,12,6.837	c1.257,2.124,3.039,3.906,5.163,5.163C15.039,13.257,13.257,15.039,12,17.163z" />
                    </svg>
                    <h6 className="mb-0">AI Assistant</h6>
                  </div>
                  <div className="ai-model-replytext">
                    <TypingEffect text={suggestion} />
                    <div className="copy-insert-button mt-2">
                      <button
                        className="custom-copy-insert"
                        title={copied ? 'Copied' : 'Copy'}
                        onClick={handleCopy}
                      >
                        {copied ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                      <button
                        className="custom-copy-insert"
                        title="Insert to Editor"
                        onClick={handleInsert}
                      >
                        <TbPlaylistAdd size={18} />
                      </button>
                    </div>
                  </div>
                </>
              ) : null}
            </section>
            <hr />
            {loading ? (
              <div className="custom-thinking-loader" disabled={loading}>
                <img src={'/images/rippleloading.svg'} height={60} />
              </div>
            ) : (
              <div className="ai-actions">
                {Object.entries(toneOptions).map(([tone, label]) => (
                  <button
                    key={tone}
                    className="ai-cancel"
                    onClick={() => handlePromptSubmit(tone)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <p className="ai-hint">Try: General, Friendly, Change tone, etc.</p>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default AiModalPopup;
